import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserCircleIcon,
  FunnelIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  DocumentTextIcon,
  EyeIcon,
  PhoneIcon,
  IdentificationIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const SessionList = ({ sessions = [] }) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Données fictives des membres pour compléter les infos de sessions
  const mockMembers = [
    { id: 'M001', memberCode: 'MEM-2023-001', firstName: 'Ahmed', lastName: 'Benaissa', name: 'Ahmed Benaissa', phone: '0551234567', email: 'ahmed.b@email.com' },
    { id: 'M002', memberCode: 'MEM-2023-002', firstName: 'Fatima', lastName: 'Khelil', name: 'Fatima Khelil', phone: '0661234567', email: 'fatima.k@email.com' },
    { id: 'M003', memberCode: 'MEM-2023-003', firstName: 'Karim', lastName: 'Messaoud', name: 'Karim Messaoud', phone: '0771234567', email: 'karim.m@email.com' },
    { id: 'M005', memberCode: 'MEM-2023-005', firstName: 'Mohamed', lastName: 'Hakim', name: 'Mohamed Hakim', phone: '0661234568', email: 'mohamed.h@email.com' },
    { id: 'M007', memberCode: 'MEM-2023-007', firstName: 'Youcef', lastName: 'Talbi', name: 'Youcef Talbi', phone: '0551234569', email: 'youcef.t@email.com' },
    { id: 'M008', memberCode: 'MEM-2023-008', firstName: 'Naima', lastName: 'Kadri', name: 'Naima Kadri', phone: '0661234569', email: 'naima.k@email.com' },
    { id: 'M010', memberCode: 'MEM-2023-010', firstName: 'Leila', lastName: 'Mebarki', name: 'Leila Mebarki', phone: '0551234570', email: 'leila.m@email.com' }
  ];
  
  // Ajouter les infos des membres aux sessions
  const enhancedSessions = sessions.map(session => {
    const member = mockMembers.find(m => m.id === session.memberId);
    return {
      ...session,
      member
    };
  });

  // Fetch members data for search (avec données fictives)
  const { data: membersData } = useQuery('members', async () => {
    try {
      const response = await axios.get('/members');
      return response.data;
    } catch (error) {
      return { members: mockMembers };
    }
  });

  const members = membersData?.members || mockMembers;

  // Filter sessions based on criteria
  const filteredSessions = enhancedSessions.filter(session => {
    // Search filter
    const member = session.member;
    const matchesSearch = !searchTerm || 
      member?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member?.memberCode?.toLowerCase().includes(searchTerm.toLowerCase());

    // Date filter
    const sessionDate = new Date(session.checkInTime);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);
    const thisMonth = new Date(today);
    thisMonth.setDate(thisMonth.getDate() - 30);

    let matchesDate = true;
    switch (dateFilter) {
      case 'today':
        matchesDate = sessionDate.toDateString() === today.toDateString();
        break;
      case 'yesterday':
        matchesDate = sessionDate.toDateString() === yesterday.toDateString();
        break;
      case 'this-week':
        matchesDate = sessionDate >= thisWeek;
        break;
      case 'this-month':
        matchesDate = sessionDate >= thisMonth;
        break;
      default:
        matchesDate = true;
    }

    // Type filter
    const matchesType = typeFilter === 'all' || session.type === typeFilter;

    // Status filter (active/completed)
    const isActive = !session.checkOutTime;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && isActive) ||
      (statusFilter === 'completed' && !isActive);

    return matchesSearch && matchesDate && matchesType && matchesStatus;
  });

  // Calculate session duration
  const getSessionDuration = (checkInTime, checkOutTime) => {
    const start = new Date(checkInTime);
    const end = checkOutTime ? new Date(checkOutTime) : new Date();
    const duration = Math.floor((end - start) / 1000 / 60); // minutes
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return { hours, minutes, total: duration };
  };

  // Format duration display
  const formatDuration = (duration) => {
    if (duration.hours > 0) {
      return `${duration.hours}h ${duration.minutes}m`;
    }
    return `${duration.minutes}m`;
  };

  // Get session status
  const getSessionStatus = (session) => {
    if (!session.checkOutTime) {
      return { status: 'active', color: 'green', text: 'En cours' };
    }
    return { status: 'completed', color: 'blue', text: 'Terminée' };
  };

  // Get session type display
  const getSessionTypeDisplay = (type) => {
    switch (type) {
      case 'check-in':
        return { text: 'Entrée', icon: ArrowRightOnRectangleIcon, color: 'green' };
      case 'check-out':
        return { text: 'Sortie', icon: ArrowLeftOnRectangleIcon, color: 'red' };
      case 'class':
        return { text: 'Cours', icon: UserCircleIcon, color: 'blue' };
      case 'training':
        return { text: 'Entraînement', icon: DocumentTextIcon, color: 'purple' };
      default:
        return { text: 'Séance', icon: ClockIcon, color: 'gray' };
    }
  };

  // Group sessions by date
  const groupSessionsByDate = (sessions) => {
    const groups = {};
    sessions.forEach(session => {
      const date = new Date(session.checkInTime).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(session);
    });
    return groups;
  };

  const sessionGroups = groupSessionsByDate(filteredSessions);

  // Calculate statistics
  const stats = {
    totalSessions: enhancedSessions.length,
    activeSessions: enhancedSessions.filter(s => !s.checkOutTime).length,
    todaySessions: enhancedSessions.filter(s => 
      new Date(s.checkInTime).toDateString() === new Date().toDateString()
    ).length,
    avgDuration: enhancedSessions.length > 0 ? 
      Math.round(enhancedSessions.reduce((sum, s) => {
        const duration = getSessionDuration(s.checkInTime, s.checkOutTime);
        return sum + duration.total;
      }, 0) / enhancedSessions.length) : 0,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Séances Actives</p>
              <p className="text-xl font-bold text-white">{stats.activeSessions}</p>
            </div>
            <div className="bg-green-500/20 p-3 rounded-xl">
              <ClockIcon className="h-5 w-5 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Aujourd'hui</p>
              <p className="text-xl font-bold text-white">{stats.todaySessions}</p>
            </div>
            <div className="bg-blue-500/20 p-3 rounded-xl">
              <CalendarDaysIcon className="h-5 w-5 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total Séances</p>
              <p className="text-xl font-bold text-white">{stats.totalSessions}</p>
            </div>
            <div className="bg-purple-500/20 p-3 rounded-xl">
              <DocumentTextIcon className="h-5 w-5 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Durée Moyenne</p>
              <p className="text-xl font-bold text-white">{Math.floor(stats.avgDuration / 60)}h {stats.avgDuration % 60}m</p>
            </div>
            <div className="bg-orange-500/20 p-3 rounded-xl">
              <ClockIcon className="h-5 w-5 text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/40" />
            <input
              type="text"
              placeholder="Rechercher par nom ou code membre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Aujourd'hui</option>
            <option value="yesterday">Hier</option>
            <option value="this-week">Cette semaine</option>
            <option value="this-month">Ce mois</option>
            <option value="all">Toute période</option>
          </select>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous types</option>
            <option value="check-in">Entrées</option>
            <option value="check-out">Sorties</option>
            <option value="class">Cours</option>
            <option value="training">Entraînements</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous statuts</option>
            <option value="active">Actives</option>
            <option value="completed">Terminées</option>
          </select>
        </div>
      </div>

      {/* Sessions List */}
      <div className="space-y-6">
        {Object.keys(sessionGroups).length > 0 ? (
          Object.entries(sessionGroups)
            .sort(([a], [b]) => new Date(b) - new Date(a))
            .map(([date, dateSessions]) => (
              <div key={date} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  {new Date(date).toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                  <span className="ml-2 text-sm text-white/60">({dateSessions.length} séances)</span>
                </h3>
                
                <div className="space-y-3">
                  {dateSessions.map((session) => {
                    const duration = getSessionDuration(session.checkInTime, session.checkOutTime);
                    const status = getSessionStatus(session);
                    const typeDisplay = getSessionTypeDisplay(session.type);
                    const TypeIcon = typeDisplay.icon;
                    
                    return (
                      <motion.div
                        key={session.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                      >
                        <div className="flex items-center space-x-4">
                          {/* Member Photo */}
                          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                            {session.member?.photo ? (
                              <img 
                                src={session.member.photo} 
                                alt={session.member.firstName} 
                                className="w-full h-full object-cover" 
                              />
                            ) : (
                              <UserCircleIcon className="h-8 w-8 text-white/40" />
                            )}
                          </div>
                          
                          {/* Member Info */}
                          <div>
                            <h4 className="font-medium text-white">
                              {session.member ? `${session.member.firstName} ${session.member.lastName}` : (session.memberName || 'Membre inconnu')}
                            </h4>
                            <p className="text-white/60 text-sm">{session.member?.memberCode || session.memberCode}</p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-6">
                          {/* Session Type */}
                          <div className="flex items-center space-x-2">
                            <TypeIcon className={`h-5 w-5 text-${typeDisplay.color}-400`} />
                            <span className="text-white/80">{typeDisplay.text}</span>
                          </div>

                          {/* Time */}
                          <div className="text-right">
                            <p className="text-white font-medium">
                              {new Date(session.checkInTime).toLocaleTimeString('fr-FR', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </p>
                            {session.checkOutTime && (
                              <p className="text-white/60 text-sm">
                                → {new Date(session.checkOutTime).toLocaleTimeString('fr-FR', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </p>
                            )}
                          </div>

                          {/* Duration */}
                          <div className="text-right">
                            <p className="text-white font-medium">{formatDuration(duration)}</p>
                            <p className="text-white/60 text-sm">durée</p>
                          </div>

                          {/* Status */}
                          <div className="flex items-center space-x-2">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              status.color === 'green' ? 'bg-green-500/20 text-green-400' :
                              status.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {status.text}
                            </span>
                            {!session.checkOutTime && (
                              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            )}
                          </div>

                          {/* Actions */}
                          <button
                            onClick={() => {
                              setSelectedSession(session);
                              setShowDetails(true);
                            }}
                            className="p-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
                            title="Voir les détails"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))
        ) : (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 text-center">
            <ClockIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Aucune séance trouvée
            </h3>
            <p className="text-white/60">
              Aucune séance ne correspond aux critères de recherche sélectionnés.
            </p>
          </div>
        )}
      </div>

      {/* Session Details Modal */}
      {showDetails && selectedSession && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-2xl w-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Détails de la séance</h2>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>

            {/* Member Info */}
            <div className="bg-white/5 rounded-2xl p-4 mb-6">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                  {selectedSession.member?.photo ? (
                    <img 
                      src={selectedSession.member.photo} 
                      alt={selectedSession.member.firstName} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <UserCircleIcon className="h-12 w-12 text-white/40" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    {selectedSession.member ? `${selectedSession.member.firstName} ${selectedSession.member.lastName}` : (selectedSession.memberName || 'Membre inconnu')}
                  </h3>
                  <p className="text-white/60">{selectedSession.member?.memberCode || selectedSession.memberCode}</p>
                </div>
              </div>
            </div>

            {/* Session Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-blue-400" />
                  <h4 className="font-medium text-white">Heure d'entrée</h4>
                </div>
                <p className="text-white/80">
                  {new Date(selectedSession.checkInTime).toLocaleString('fr-FR')}
                </p>
              </div>

              {selectedSession.checkOutTime && (
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <ClockIcon className="h-5 w-5 text-red-400" />
                    <h4 className="font-medium text-white">Heure de sortie</h4>
                  </div>
                  <p className="text-white/80">
                    {new Date(selectedSession.checkOutTime).toLocaleString('fr-FR')}
                  </p>
                </div>
              )}

              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <DocumentTextIcon className="h-5 w-5 text-purple-400" />
                  <h4 className="font-medium text-white">Type</h4>
                </div>
                <p className="text-white/80">{getSessionTypeDisplay(selectedSession.type).text}</p>
              </div>

              <div className="bg-white/5 rounded-xl p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-green-400" />
                  <h4 className="font-medium text-white">Durée</h4>
                </div>
                <p className="text-white/80">
                  {formatDuration(getSessionDuration(selectedSession.checkInTime, selectedSession.checkOutTime))}
                </p>
              </div>
            </div>

            {/* Notes */}
            {selectedSession.notes && (
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <DocumentTextIcon className="h-5 w-5 text-yellow-400" />
                  <h4 className="font-medium text-white">Notes</h4>
                </div>
                <p className="text-white/80 whitespace-pre-wrap">
                  {selectedSession.notes}
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setShowDetails(false)}
                className="bg-white/10 text-white px-4 py-2 rounded-xl hover:bg-white/20 transition-colors"
              >
                Fermer
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SessionList; 